import React from "react";
import PageWrapper, { PageHero } from "../components/PageWrapper";
import LeadForm from "../components/LeadForm";
import {
  Plane, Hotel, Stamp, ShieldCheck, Bus, ClipboardCheck, Users, Briefcase,
} from "lucide-react";

const SEGMENTS = [
  { icon: Users, title: "Academic Delegations", desc: "Conferences, summits, and policy delegations across continents." },
  { icon: Briefcase, title: "Conference Travel", desc: "Speaker, faculty, and institutional cohort travel coordination." },
  { icon: Users, title: "Institutional Group Travel", desc: "Multi-cohort, multi-stop institutional movement at scale." },
  { icon: Briefcase, title: "Corporate Educational Travel", desc: "Leadership, training, and exposure travel for corporate education." },
];

const SERVICES = [
  { icon: Plane, title: "Flights" },
  { icon: Hotel, title: "Hotels" },
  { icon: Stamp, title: "Visa Assistance" },
  { icon: ShieldCheck, title: "Travel Insurance" },
  { icon: Bus, title: "Ground Transfers" },
  { icon: ClipboardCheck, title: "End-to-End Coordination" },
];

export default function CorporateTravelPage() {
  return (
    <PageWrapper testId="corporate-travel-page">
      <PageHero
        eyebrow="Corporate & Institutional Travel"
        title="Premium institutional travel — engineered, not improvised."
        description="From academic delegations and conference travel to institutional group movement and corporate educational travel — handled end-to-end by a single partner."
        image="https://images.pexels.com/photos/6726195/pexels-photo-6726195.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="bg-white py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
              Segments
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight">
              Four segments. One operating standard.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {SEGMENTS.map((s, i) => (
              <div
                key={s.title}
                data-testid={`travel-segment-${i}`}
                className="border border-slate-200 p-7"
              >
                <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center">
                  <s.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="mt-6 font-display text-lg text-navy">
                  {s.title}
                </div>
                <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F8FAFC] py-24 sm:py-28">
        <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold-500">
            Services
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light text-navy tracking-tight max-w-3xl">
            Every operational layer, owned by a single partner.
          </h2>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
            {SERVICES.map((s, i) => (
              <div
                key={s.title}
                data-testid={`travel-service-${i}`}
                className="card-lift bg-white border border-slate-200 p-7 text-center"
              >
                <div className="mx-auto h-11 w-11 bg-navy text-gold flex items-center justify-center">
                  <s.icon size={18} strokeWidth={1.5} />
                </div>
                <div className="mt-5 font-display text-base text-navy">
                  {s.title}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white py-24 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.22em] text-gold">
              Coordinate with us
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-light tracking-tight">
              Plan your next institutional movement.
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed max-w-md">
              From a 12-person delegation to a 400-person institutional cohort,
              we own the coordination so your team owns the agenda.
            </p>
          </div>
          <div className="lg:col-span-7">
            <LeadForm
              inquiryType="corporate"
              variant="dark"
              testIdPrefix="travel-lead-form"
              title="Plan an institutional movement"
              subtitle="Tell us about your delegation. We'll come back with a structured plan."
            />
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}
